import * as React from "react";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import dynamic from "next/dynamic";

import { CampaignFinderProps } from "./type";
import { useCampaignFinder } from "./useCampaignFinder";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Search as SearchIcon } from "@/icons/outlined";

const DonationFormCard = dynamic(() => import("../DonationFormCard/DonationFormCard"));
const FinderCard = dynamic(() => import("../FinderCard/FinderCard"));

const CampaignFinder: React.FunctionComponent<React.PropsWithChildren<CampaignFinderProps>> = (props) => {
  const {
    handleChange,
    loading,
    refetchOrFetchMoreLoading,
    teamDonationForms,
    fundraiserDonationForms,
    loadMore,
    hasMoreData,
    search,
  } = useCampaignFinder(props);

  const { hideTitle, color, currency, linksTarget } = props;
  const { t } = useTranslate();

  const finderHasTeamOrFundraiser = teamDonationForms.length > 0 || fundraiserDonationForms.length > 0;
  const hasTeams = teamDonationForms.length > 0;
  const hasIndividuals = fundraiserDonationForms.length > 0;

  if (!finderHasTeamOrFundraiser && !(search.length > 0) && !refetchOrFetchMoreLoading) {
    return null;
  }

  return (
    <Paper
      sx={(theme) => ({
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
      })}
    >
      <Grid container spacing={2}>
        {!hideTitle && (
          <Grid item xs={12}>
            <DashboardTypography variant="body1">{t("donationForm", "findACampaign")}</DashboardTypography>
          </Grid>
        )}
        <Grid item xs={12}>
          <DashboardTextField
            debounced={300}
            size="medium"
            data-test="campaign-finder-search"
            onChange={handleChange}
            endAdornment={
              <DashboardIcon vibe="text-form-quiet">
                <SearchIcon />
              </DashboardIcon>
            }
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        {!refetchOrFetchMoreLoading && !finderHasTeamOrFundraiser && (
          <Grid item xs={12}>
            <DashboardTypography variant="subtitle2">{t("dashboard", "noData")}</DashboardTypography>
          </Grid>
        )}
        {finderHasTeamOrFundraiser && (
          <Grid item xs={12} data-test="campaign-finder-results">
            <FinderCard isLoading={refetchOrFetchMoreLoading}>
              {hasTeams && (
                <Grid
                  xs={12}
                  sm={6}
                  sx={{
                    paddingInlineEnd: { md: 1 },
                  }}
                >
                  {teamDonationForms.map((donationForm, index) => (
                    <DonationFormCard
                      key={index}
                      donationForm={donationForm}
                      color={color}
                      currency={currency}
                      src={donationForm.logoUrl}
                      target={linksTarget}
                    />
                  ))}
                </Grid>
              )}
              {hasIndividuals && (
                <Grid
                  xs={12}
                  sm={6}
                  sx={(theme) => ({
                    [theme.breakpoints.up("sm")]: {
                      borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
                      paddingInlineStart: 1,
                    },
                  })}
                >
                  {fundraiserDonationForms.map((donationForm, index) => (
                    <DonationFormCard
                      key={index}
                      donationForm={donationForm}
                      color={color}
                      currency={currency}
                      src={donationForm.logoUrl}
                      target={linksTarget}
                    />
                  ))}
                </Grid>
              )}
            </FinderCard>
          </Grid>
        )}
        {loading && (
          <Grid item xs={12}>
            <Grid container spacing={1}>
              {[...Array(6)].map((_: unknown, index: number) => (
                <Grid item xs={6} key={index}>
                  <Skeleton
                    variant="rectangular"
                    height={63}
                    animation="wave"
                    sx={(theme) => ({
                      borderRadius: theme.radius(0.5),
                    })}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        {finderHasTeamOrFundraiser && hasMoreData && !refetchOrFetchMoreLoading && (
          <Grid
            container
            sx={(theme) => ({
              marginTop: theme.spacing(2),
            })}
          >
            <DashboardButton fullWidth onClick={loadMore} vibe="brand" isLoading={loading} data-test="view-more-button">
              {t("common", "loadMore")}
            </DashboardButton>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default CampaignFinder;
